/**
 * ATTEST-UI-1 screen 3 — the refusal, as a first-class surface.
 *
 * REFACTOR-SPLIT (Bundle 2 item 2): 317 → 121 lines. Nothing was dropped — the
 * engine-facing half moved to `RefusalDetails.tsx` and the headline choice to
 * `refusalHeadline.ts`. Both are imported below.
 *
 * This is the product, not an error state. A CPA who asks for a letter that
 * cannot be written has to leave this screen able to answer the lender: which
 * guardrail fired, which sentence tripped it, what the profession says about
 * it, and what may be said instead. A toast cannot carry that, and a bare 422
 * teaches nobody anything.
 *
 * Everything rendered here comes from the refusal payload. There is no local
 * copy of the deny list, no local rationale text, and no mapping from code to
 * advice — the policy owns all of it, so a policy edit changes this screen
 * without a frontend deploy.
 *
 * What it does *not* do any more is render the whole payload at once (Bundle 2
 * item 2). The wall now leads with the human half — what happened, and the one
 * thing to do about it — and the engine-facing half lives one click away in
 * `RefusalDetails`. The headline itself is chosen in `refusalHeadline`, which
 * is where the evidence codes are read: the same refusal means "your document
 * disagrees", "you attached nothing", or "we could not read it", and the CPA
 * should never be sent back to a document they never uploaded.
 */
import type { AttestRefusal } from '@/components/attest/api'
import { RefusalDetails } from '@/components/attest/RefusalDetails'
import { guidanceFor, type AttestStepKey } from '@/components/attest/refusalGuidance'
import { headlineFor } from '@/components/attest/refusalHeadline'
import { Button } from '@/components/ui/button'

export interface RefusalPanelProps {
  refusal: AttestRefusal
  /** Optional escape hatch back to the step that produced the refusal. */
  onBack?: () => void
  backLabel?: string
  /**
   * Deep-link to the wizard step the refusal is attributable to
   * (ATTEST-INDEPENDENCE-MATRIX-1 E.1). Omitted where the panel is not hosted
   * inside the wizard — the pointer still names the step, it just doesn't jump.
   */
  onGoToStep?: (step: AttestStepKey, fieldAnchor?: string) => void
  /**
   * The field to land on within that step (ATTEST-UX-2 D2). Resolved by the
   * host, because it depends on the form's current state — this panel only
   * knows the refusal. Absent → the jump stays step-level, as before.
   */
  fieldAnchor?: string
}

export function RefusalPanel({
  refusal, onBack, backLabel = 'Revise the request', onGoToStep, fieldAnchor,
}: RefusalPanelProps) {
  const guidance = guidanceFor(refusal.error_code)
  // Pulled out as its own const so the closure below narrows it: a guidance
  // whose step is null still shows its remedy, but must not offer a jump.
  const guidanceStep = guidance?.step ?? null

  return (
    <div
      data-testid="attest-refusal"
      data-error-code={refusal.error_code}
      role="alert"
      className="rounded-lg border-2 border-red-300 bg-red-50 p-5"
    >
      {/* The code chip used to sit here, opposite the headline. It moved into
          the Details block: it is the first thing an engineer wants and roughly
          the last thing a CPA can use, and giving it equal billing with the
          headline said the opposite. `data-error-code` above is unchanged, so
          anything keying off the wall itself still reads it. */}
      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-widest text-red-700">Refused</p>
        <h2 className="mt-1 text-lg font-bold text-red-900">
          {headlineFor(refusal)}
        </h2>
      </div>

      {refusal.user_message && (
        <p className="mt-3 text-sm text-red-900" data-testid="attest-refusal-user-message">
          {refusal.user_message}
        </p>
      )}
      {refusal.message && (
        <p className="mt-2 text-sm text-gray-800" data-testid="attest-refusal-reason">
          {refusal.message}
        </p>
      )}

      {guidance && (
        <div
          data-testid="attest-refusal-guidance"
          data-step={guidanceStep ?? undefined}
          className="mt-4 rounded border border-red-200 bg-white p-3"
        >
          <p className="text-sm text-gray-900">
            {guidanceStep && (
              <span className="font-semibold">Fix it on {guidance.stepLabel}: </span>
            )}
            {guidance.remedy}
          </p>
          {onGoToStep && guidanceStep && (
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              data-testid="attest-refusal-goto-step"
              onClick={() => onGoToStep(guidanceStep, fieldAnchor)}
            >
              Go to {guidance.stepLabel}
            </Button>
          )}
        </div>
      )}

      <RefusalDetails refusal={refusal} />

      {onBack && (
        <div className="mt-5">
          <Button variant="outline" onClick={onBack}>{backLabel}</Button>
        </div>
      )}
    </div>
  )
}
